import { cn, Logo, LogoMark } from "@nexxt/ui";
import type { ReactNode } from "react";
import type { AppId } from "../suite-map";

interface AppWindowProps {
  app: AppId;
  /** The page is dark: the window follows, with the dark tokens of its product. */
  dark: boolean;
  /** The address in the bar: the window is a picture of the app, not the app. */
  title: string;
  /** The places of the app's sidebar; none on a phone. */
  places?: readonly string[];
  className?: string;
  children: ReactNode;
}

/**
 * A picture of one app of the suite (ADR-286): its tokens through `data-product` (Backstage keeps the root's), its
 * logo, its places. Hidden from assistive technology: the chapter beside it says in words what it shows.
 */
export function AppWindow({ app, dark, title, places, className, children }: AppWindowProps) {
  return (
    <div
      aria-hidden
      className={cn("app-window", dark && "dark", className)}
      data-product={app === "backstage" || app === "guestlist" ? undefined : app}
    >
      <div className="app-window-chrome">
        <i />
        <i />
        <i />
        <span>{title}</span>
      </div>
      <div className="app-window-bar">
        <AppMark app={app} />
      </div>
      <div className="app-window-body">
        {places && (
          <ul className="app-window-places">
            {places.map((place, i) => (
              <li key={place} className={cn(i === 0 && "is-current")}>
                {place}
              </li>
            ))}
          </ul>
        )}
        <div className="app-window-main">{children}</div>
      </div>
    </div>
  );
}

/**
 * The mark of the app's bar. Guestlist has no line of its own in the generated logo yet (its app brings it): the
 * same symbol and name, and its product in the letters of the context line.
 */
export function AppMark({ app }: { app: AppId }) {
  if (app !== "guestlist") return <LogoMark product={app} />;
  return (
    <div className="flex items-center gap-2">
      <Logo variant="symbol" className="size-6" />
      <span className="flex items-baseline gap-1.5">
        <Logo variant="wordmark" className="h-3 w-auto" />
        <span className="app-mark-product">GUESTLIST</span>
      </span>
    </div>
  );
}
